import React from 'react'
import { Badge, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch } from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import type { NaceCodeItem, NaceCodeCreateUpdatePayload } from '../api/naceCodesApi'
import { naceCodesApi, LANGUAGES } from '../api/naceCodesApi'

type Form = {
  code: string
  name: string
  language: string
  level: string
  section: string
  sortOrder: number
  isActive: boolean
}

export default function NaceCodesPage() {
  return (
    <CrudPage<NaceCodeItem, Form, NaceCodeCreateUpdatePayload, NaceCodeCreateUpdatePayload>
      title="NACE Kodları"
      subtitle="NACE kodlarını dil bazlı yönetin (tr, en, az)"
      primaryActionLabel="Yeni NACE Kodu"
      list={async (p) =>
        naceCodesApi.list({
          lang: p.filters.lang || 'tr',
          search: p.search || undefined,
          page: p.page,
          pageSize: p.pageSize,
          sortBy: (p.sortBy as 'code' | 'name' | 'section' | 'sortOrder') ?? undefined,
          sortDir: (p.sortDir as 'asc' | 'desc') ?? undefined,
        })
      }
      getRowId={(o) => o.id}
      columns={[
        {
          key: 'code',
          header: 'Kod',
          sortable: true,
          widthClassName: 'w-[100px]',
          render: (o) => <span className="font-medium">{o.code}</span>,
        },
        {
          key: 'name',
          header: 'Ad',
          sortable: true,
          widthClassName: 'w-[280px]',
          render: (o) => o.name,
        },
        {
          key: 'language',
          header: 'Dil',
          sortable: false,
          widthClassName: 'w-[80px]',
          render: (o) => o.language.toUpperCase(),
        },
        {
          key: 'section',
          header: 'Bölüm',
          sortable: true,
          widthClassName: 'w-[80px]',
          render: (o) => o.section ?? '—',
        },
        {
          key: 'isActive',
          header: 'Aktif',
          sortable: false,
          widthClassName: 'w-[90px]',
          render: (o) =>
            o.isActive ? (
              <Badge variant="success">Aktif</Badge>
            ) : (
              <Badge variant="warning">Pasif</Badge>
            ),
        },
        {
          key: 'sortOrder',
          header: 'Sıra',
          sortable: true,
          widthClassName: 'w-[70px]',
          render: (o) => o.sortOrder,
        },
        {
          key: 'updatedAt',
          header: 'Güncellenme',
          sortable: false,
          widthClassName: 'w-[160px]',
          render: (o) =>
            o.updatedAt
              ? new Date(o.updatedAt).toLocaleString('tr-TR')
              : o.createdAt
                ? new Date(o.createdAt).toLocaleString('tr-TR')
                : '—',
        },
      ]}
      searchPlaceholder="Ara (kod, ad, bölüm)…"
      filters={[
        {
          id: 'lang',
          label: 'Dil',
          value: 'tr',
          options: [
            { label: 'Türkçe', value: 'tr' },
            { label: 'English', value: 'en' },
            { label: 'Azərbaycan', value: 'az' },
          ],
          widthClassName: 'min-w-[140px]',
        },
      ]}
      initialPageSize={10}
      modalTitles={{ create: 'Yeni NACE Kodu', edit: 'NACE Kodunu Düzenle' }}
      getCreateDefaultForm={() => ({
        code: '',
        name: '',
        language: 'tr',
        level: '',
        section: '',
        sortOrder: 0,
        isActive: true,
      })}
      getEditForm={(o) => ({
        code: o.code,
        name: o.name,
        language: o.language,
        level: o.level ?? '',
        section: o.section ?? '',
        sortOrder: o.sortOrder,
        isActive: o.isActive,
      })}
      validate={(f) => ({
        code: !f.code.trim() ? 'Kod zorunludur' : undefined,
        name: !f.name.trim() ? 'Ad zorunludur' : undefined,
        language: !f.language ? 'Dil seçiniz' : undefined,
      })}
      renderForm={({ form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <div className="grid gap-4">
          <Input
            label="Kod"
            value={form.code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, code: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.code}
            helperText={touched ? errors.code : undefined}
            required
            disabled={isSubmitting}
            placeholder="Örn: A, B, C"
            leftIcon={<i className="material-symbols-outlined text-lg">code</i>}
          />
          <Input
            label="Ad"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, name: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            required
            disabled={isSubmitting}
            placeholder="NACE açıklaması"
            leftIcon={<i className="material-symbols-outlined text-lg">description</i>}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-neutral-700">Dil</label>
            <Select
              value={form.language}
              onValueChange={(v: string) => setForm((s) => ({ ...s, language: v }))}
              disabled={isSubmitting}
            >
              <SelectTrigger className="w-full" error={touched && !!errors.language}>
                <SelectValue placeholder="Dil seçin" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {touched && errors.language && (
              <p className="mt-1 text-sm text-red-600">{errors.language}</p>
            )}
          </div>
          <Input
            label="Bölüm (Section)"
            value={form.section}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, section: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            disabled={isSubmitting}
            placeholder="Örn: A, B"
          />
          <Input
            label="Seviye (Level)"
            value={form.level}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, level: e.target.value }))
            }
            disabled={isSubmitting}
            placeholder="Örn: section"
          />
          <Input
            label="Sıra"
            type="number"
            value={String(form.sortOrder)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setForm((s) => ({ ...s, sortOrder: parseInt(e.target.value, 10) || 0 }))
            }
            onBlur={() => setTouched(true)}
            disabled={isSubmitting}
            placeholder="0"
          />
          <Switch
            checked={form.isActive}
            onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, isActive: v }))}
            label="Aktif"
            disabled={isSubmitting}
          />
        </div>
      )}
      buildCreatePayload={(f) => ({
        code: f.code.trim(),
        name: f.name.trim(),
        language: f.language,
        level: f.level.trim() || undefined,
        section: f.section.trim() || undefined,
        sortOrder: f.sortOrder,
        isActive: f.isActive,
      })}
      buildUpdatePayload={(f) => ({
        code: f.code.trim(),
        name: f.name.trim(),
        language: f.language,
        level: f.level.trim() || undefined,
        section: f.section.trim() || undefined,
        sortOrder: f.sortOrder,
        isActive: f.isActive,
      })}
      create={(payload) => naceCodesApi.create(payload)}
      update={(id, payload) => naceCodesApi.update(Number(id), payload)}
      remove={(id) => naceCodesApi.delete(Number(id))}
      getCreatedId={(res) => res?.id}
    />
  )
}
